import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_LINKS = [
    { label: 'Mirror Circle Membership', href: '/membership' },
    { label: 'Events', href: '/events' },
    { label: 'Library', href: '/library' },
    { label: 'Forum', href: '/forum' }
];

const DEFAULT_SOCIAL = [
    { label: 'YouTube', href: 'https://youtube.com' },
    { label: 'Facebook Community', href: 'https://facebook.com' }
];

export default function Footer() {
    const { t } = useTranslation();
    const [s, setS] = useState({});
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterName, setNewsletterName] = useState('');
    const [subscribing, setSubscribing] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/site-settings`);
            setS(data || {});
        } catch (err) { /* defaults */ }
    }, []);

    useEffect(() => {
        load();
        const onChange = () => load();
        window.addEventListener('site-settings-changed', onChange);
        return () => window.removeEventListener('site-settings-changed', onChange);
    }, [load]);

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        if (!newsletterEmail.trim()) return;
        setSubscribing(true);
        try {
            await axios.post(`${API_URL}/api/newsletter/subscribe`, {
                email: newsletterEmail.trim(),
                name: newsletterName.trim() || null
            });
            toast.success("You're on the list. Welcome.");
            setNewsletterEmail('');
            setNewsletterName('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not subscribe. Please try again.');
        } finally {
            setSubscribing(false);
        }
    };

    const links = Array.isArray(s.footer_links) && s.footer_links.length ? s.footer_links : DEFAULT_LINKS;
    const social = Array.isArray(s.footer_social) && s.footer_social.length ? s.footer_social : DEFAULT_SOCIAL;
    const title = s.site_title || 'Methode 4 Life';
    const tagline = s.footer_tagline || 'Remember Who You Are. A school of Consciousness, not a self-help programme, not a therapy, not a spiritual trend.';
    const footerText = s.footer_text || `© ${new Date().getFullYear()} ${title}. All rights reserved.`;

    const isExternal = (href) => /^https?:\/\//i.test(href || '');

    const renderLink = (l, i, prefix) => {
        if (!l?.href) return null;
        const cls = 'text-sm text-[#F9F6F0]/80 hover:text-[#D4AF37] transition-colors';
        return isExternal(l.href) ? (
            <a key={`${prefix}-${i}`} href={l.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`footer-${prefix}-${i}`}>
                {l.label}
            </a>
        ) : (
            <Link key={`${prefix}-${i}`} to={l.href} className={cls} data-testid={`footer-${prefix}-${i}`}>
                {l.label}
            </Link>
        );
    };

    return (
        <footer className="bg-[#1A1A2E] text-[#F9F6F0]" data-testid="footer">
            <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="md:col-span-2">
                        <h3 className="font-serif text-2xl mb-4">{title}</h3>
                        <p className="font-sans text-sm text-[#D8B4E2] mb-6 leading-relaxed max-w-md whitespace-pre-wrap">
                            {tagline}
                        </p>

                        {/* Newsletter signup */}
                        <div className="max-w-md" data-testid="footer-newsletter">
                            <h4 className="font-sans text-sm uppercase tracking-widest text-[#D4AF37] mb-3 flex items-center gap-2">
                                <Mail className="w-4 h-4" /> {t('footer.stay_in_the_circle')}
                            </h4>
                            <p className="text-xs text-[#F9F6F0]/70 mb-3">
                                {t('footer.newsletter_intro')}
                            </p>
                            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
                                <input
                                    type="text"
                                    value={newsletterName}
                                    onChange={(e) => setNewsletterName(e.target.value)}
                                    placeholder={t('landing.partnership_form_name')}
                                    className="w-full bg-white/5 border border-[#F9F6F0]/20 focus:border-[#D4AF37] rounded-sm px-3 py-2 text-sm text-[#F9F6F0] placeholder-[#F9F6F0]/40 outline-none"
                                    data-testid="newsletter-name"
                                />
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        required
                                        value={newsletterEmail}
                                        onChange={(e) => setNewsletterEmail(e.target.value)}
                                        placeholder={t('footer.email_placeholder')}
                                        className="flex-1 bg-white/5 border border-[#F9F6F0]/20 focus:border-[#D4AF37] rounded-sm px-3 py-2 text-sm text-[#F9F6F0] placeholder-[#F9F6F0]/40 outline-none"
                                        data-testid="newsletter-email"
                                    />
                                    <button
                                        type="submit"
                                        disabled={subscribing}
                                        className="bg-[#D4AF37] text-[#1A1A2E] hover:bg-[#F9F6F0] transition rounded-sm px-4 py-2 text-xs uppercase tracking-widest font-medium disabled:opacity-60"
                                        data-testid="newsletter-submit"
                                    >
                                        {subscribing ? '…' : t('footer.subscribe')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-sans text-sm uppercase tracking-widest text-[#D4AF37] mb-4">Explore</h4>
                        <ul className="space-y-3">
                            {links.map((l, i) => <li key={i}>{renderLink(l, i, 'link')}</li>)}
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-sans text-sm uppercase tracking-widest text-[#D4AF37] mb-4">Connect</h4>
                        <ul className="space-y-3">
                            {social.map((l, i) => <li key={i}>{renderLink(l, i, 'social')}</li>)}
                        </ul>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-[#F9F6F0]/10 text-center">
                    <p className="font-sans text-xs text-[#F9F6F0]/50 whitespace-pre-wrap" data-testid="footer-bottom-text">
                        {footerText}
                    </p>
                </div>
            </div>
        </footer>
    );
}
